import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Trash2, Plus, ShieldOff, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * UnitedBlacklist
 * Lista los @usernames de Telegram que el bot IGNORA en grupos de la Cartera United.
 * - Visible solo para admins.
 * - El bot no procesa fotos/PDFs de estos usuarios (silencio total, no crea receipt).
 */
export default function UnitedBlacklist() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [username, setUsername] = useState('');
  const [reason, setReason] = useState('');
  const [adding, setAdding] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/united/blacklist`);
      setItems(r.data.items || []);
    } catch (e) {
      console.error('Error cargando blacklist:', e);
      toast.error('No se pudo cargar la lista de @s ignorados');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleAdd = async () => {
    const clean = username.trim().replace(/^@/, '');
    if (clean.length < 3) {
      toast.error('Username mínimo 3 caracteres');
      return;
    }
    setAdding(true);
    try {
      const r = await axios.post(`${API}/api/united/blacklist`, {
        username: clean,
        reason: reason.trim() || null,
      });
      if (r.data.duplicate) {
        toast.info(`@${clean} ya estaba en la lista`);
      } else {
        toast.success(`@${clean} agregado a la blacklist`);
      }
      setUsername('');
      setReason('');
      await load();
    } catch (e) {
      const msg = e.response?.data?.detail || e.message;
      toast.error(`Error: ${msg}`);
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`¿Sacar @${name} de la blacklist? El bot volverá a procesar sus comprobantes.`)) return;
    setDeletingId(id);
    try {
      await axios.delete(`${API}/api/united/blacklist/${id}`);
      toast.success(`@${name} removido de la blacklist`);
      await load();
    } catch (e) {
      const msg = e.response?.data?.detail || e.message;
      toast.error(`Error: ${msg}`);
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <Card data-testid="united-blacklist-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ShieldOff className="w-5 h-5 text-orange-600" />
          Cartera United · @usernames ignorados
          <Badge variant="secondary" className="ml-2">{items.length}</Badge>
        </CardTitle>
        <CardDescription>
          El bot ignora silenciosamente las fotos/PDFs enviados por estos usuarios en grupos de la cartera United.
          No se crea receipt y el usuario no recibe respuesta.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Formulario agregar */}
        <div className="flex flex-col sm:flex-row gap-2 items-start sm:items-end">
          <div className="flex-1">
            <label className="text-xs font-medium text-gray-600 dark:text-gray-400">@username</label>
            <Input
              data-testid="blacklist-username-input"
              placeholder="@tudemune"
              value={username}
              onChange={e => setUsername(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleAdd()}
            />
          </div>
          <div className="flex-1">
            <label className="text-xs font-medium text-gray-600 dark:text-gray-400">Motivo (opcional)</label>
            <Input
              data-testid="blacklist-reason-input"
              placeholder="Ej: Contabilidad paralela Google Drive"
              value={reason}
              onChange={e => setReason(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleAdd()}
            />
          </div>
          <Button
            data-testid="blacklist-add-btn"
            onClick={handleAdd}
            disabled={adding || username.trim().length < 3}
            className="bg-orange-600 hover:bg-orange-700 text-white whitespace-nowrap"
          >
            {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            {!adding && <span className="ml-1">Agregar</span>}
          </Button>
        </div>

        {/* Lista */}
        {loading ? (
          <div className="flex items-center justify-center py-6 text-gray-500">
            <Loader2 className="w-5 h-5 animate-spin mr-2" />
            Cargando...
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-6 text-sm text-gray-500" data-testid="blacklist-empty">
            No hay @usernames en la blacklist.
          </div>
        ) : (
          <div className="space-y-1.5" data-testid="blacklist-items">
            {items.map(it => (
              <div
                key={it.id}
                data-testid={`blacklist-item-${it.username_lower}`}
                className="flex items-center justify-between px-3 py-2 rounded-md bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700"
              >
                <div className="flex-1 min-w-0">
                  <div className="font-mono text-sm font-semibold text-orange-700 dark:text-orange-400">
                    @{it.username_lower}
                  </div>
                  {it.reason && (
                    <div className="text-xs text-gray-600 dark:text-gray-400 mt-0.5 truncate">{it.reason}</div>
                  )}
                  <div className="text-[10px] text-gray-400 mt-0.5">
                    Agregado por {it.created_by} · {new Date(it.created_at).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })}
                  </div>
                </div>
                <Button
                  data-testid={`blacklist-delete-${it.username_lower}`}
                  size="sm"
                  variant="ghost"
                  className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950"
                  onClick={() => handleDelete(it.id, it.username_lower)}
                  disabled={deletingId === it.id}
                >
                  {deletingId === it.id
                    ? <Loader2 className="w-4 h-4 animate-spin" />
                    : <Trash2 className="w-4 h-4" />}
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
